import React, { FunctionComponent, useState } from 'react'
import { FieldRenderProps } from 'react-final-form'
import { LayoutChangeEvent, TextStyle, ViewStyle } from 'react-native'

import { get, includes, uniq, without } from 'lodash-es'
import styled from 'styled-components/native'

import { CheckboxButtonType } from '@lyrahealth-inc/shared-app-logic'

import { useAccessibilityFocus } from '../../hooks/useAccessibilityFocus'
import { BaseInput } from '../formElements/BaseInput'
import {
  Checkbox,
  checkBoxButtonTypeHasOutline,
  CheckImageComponentProps,
  GridCheckboxSize,
} from '../formElements/Checkbox'

import type * as CSS from 'csstype'

const GroupContainer = styled.View<{ buttonType: CheckboxButtonType; gridButtonsGap: number }>(
  ({ buttonType, gridButtonsGap }) =>
    buttonType === CheckboxButtonType.GRID
      ? {
          alignSelf: 'center',
          flexDirection: 'row',
          flexWrap: 'wrap',
          margin: `-${gridButtonsGap / 2}px`,
        }
      : {},
)

export const CheckboxGroup: FunctionComponent<CheckBoxGroupProps> = ({
  value,
  onChange,
  name,
  label,
  labelSecondary,
  subLabel,
  largeLabel,
  largeSubLabel,
  labelAlignment,
  error,
  options,
  readOnly,
  buttonType = CheckboxButtonType.TEXT_OUTLINE,
  active,
  baseInputStyle,
  labelStyle,
  labelContainerStyle,
  inputContainerStyle,
  customTextStyle,
  noneOfTheAboveCheckboxId,
  selectAllCheckboxId,
  gridButtonsGap = 16,
  parentContainerWidth,
  limitSelected,
  hideBaseInput,
  gridCheckboxSize,
  checkboxInputStyle,
  checkboxStyle,
  customTextColorForUnselectedCheckboxTextOutlineButtonType,
}) => {
  const [previouslySelectedValue, setPreviouslySelectedValue] = useState<string>()
  const [focusRef] = useAccessibilityFocus({ active, delay: 200 })
  const handleCheckboxPress = (pressedId: string) => {
    const wasChecked = includes(value, pressedId)

    // handle select all option
    if (selectAllCheckboxId && pressedId === selectAllCheckboxId) {
      if (!wasChecked) {
        // Select all was checked - select all options
        onChange(options.map((opt) => opt.id))
      } else {
        // Select all was unchecked - deselect all options
        onChange([])
      }
      return
    }

    // handle none of the above option
    if (noneOfTheAboveCheckboxId) {
      if (pressedId === noneOfTheAboveCheckboxId && !wasChecked) {
        // if none of the above option has been checked,
        // make it the only checked option
        value = [noneOfTheAboveCheckboxId]
      } else if (pressedId !== noneOfTheAboveCheckboxId && !wasChecked && includes(value, noneOfTheAboveCheckboxId)) {
        // if another option has been checked and none of the above is currently a checked option,
        // remove it from the checked options
        value = without(value, noneOfTheAboveCheckboxId)
      }
    }

    // When unchecking an individual item, also uncheck "Select all" if it's currently checked
    if (selectAllCheckboxId && wasChecked && includes(value, selectAllCheckboxId)) {
      value = without(value, selectAllCheckboxId)
    }

    let newValue = wasChecked ? without(value, pressedId) : uniq([...(value || []), pressedId])
    const groupsChecked = uniq(
      newValue.map((selectedId: string) =>
        get(
          options.find(({ id }) => selectedId === id),
          'group',
        ),
      ),
    )

    // If selected limit is reached we should unselect the previously selected value
    if (!wasChecked && limitSelected && limitSelected <= value.length) {
      if (previouslySelectedValue) {
        newValue = without(newValue, previouslySelectedValue)
      } else {
        newValue = without(newValue, value[value.length - 1])
      }
    }

    !wasChecked && setPreviouslySelectedValue(pressedId)
    onChange(groupsChecked.length > 1 ? [pressedId] : newValue)
  }

  const [checkboxGroupWidth, setCheckboxGroupWidth] = useState(0)
  const onLayout = (e: LayoutChangeEvent) => {
    const { width } = e.nativeEvent.layout
    setCheckboxGroupWidth(width)
  }

  const CHECKBOX_GROUP_LABEL_ID = `${name}-label`

  return (
    <BaseInput
      labelRef={focusRef}
      label={label}
      labelSecondary={labelSecondary}
      subLabel={subLabel}
      largeLabel={largeLabel}
      largeSubLabel={largeSubLabel}
      labelAlignment={labelAlignment}
      error={error}
      name={name}
      labelNativeID={CHECKBOX_GROUP_LABEL_ID}
      {...(name && { errorMessageId: `${name}-error-message` })}
      style={{
        opacity: buttonType === CheckboxButtonType.GRID && checkboxGroupWidth === 0 ? 0 : undefined,
        ...baseInputStyle,
      }}
      labelStyle={labelStyle}
      labelContainerStyle={{
        ...(buttonType === CheckboxButtonType.CHECKBOX_DESCRIPTION_OUTLINE && { marginBottom: 8 }),
        ...labelContainerStyle,
      }}
      customTextStyle={customTextStyle}
    >
      <GroupContainer
        buttonType={buttonType}
        gridButtonsGap={gridButtonsGap}
        onLayout={onLayout}
        role={'group'}
        accessibilityLabelledBy={CHECKBOX_GROUP_LABEL_ID}
        {...(error && name && { 'aria-describedby': `${name}-error-message` })}
      >
        {options.map(
          ({
            id,
            text,
            description,
            badge,
            ImageComponent,
            readOnly: readOnlyPerCheckbox,
            SubTextField,
            titleColor,
          }) => (
            <Checkbox
              key={id}
              checked={includes(value, id)}
              onPress={() => handleCheckboxPress(id)}
              name={`${name}_${id}`}
              title={text}
              titleColor={titleColor}
              description={
                typeof description === 'string' || (description && 'text' in description) ? description : undefined
              }
              readOnly={readOnly || readOnlyPerCheckbox}
              buttonType={buttonType}
              badge={badge}
              baseInputStyle={checkboxInputStyle ?? { marginBottom: checkBoxButtonTypeHasOutline[buttonType] ? 8 : 16 }}
              inputContainerStyle={inputContainerStyle}
              gridButtonsGap={gridButtonsGap}
              gridCheckboxSize={gridCheckboxSize}
              parentContainerWidth={
                parentContainerWidth && checkboxGroupWidth === 0 ? parentContainerWidth : checkboxGroupWidth
              }
              ImageComponent={ImageComponent}
              hideBaseInput={hideBaseInput}
              checkboxStyle={checkboxStyle}
              customErrorMessageId={error && name ? `${name}-error-message` : undefined}
              SubTextField={SubTextField}
              customTextColorForUnselectedCheckboxTextOutlineButtonType={
                customTextColorForUnselectedCheckboxTextOutlineButtonType
              }
            />
          ),
        )}
      </GroupContainer>
    </BaseInput>
  )
}

export const CheckboxGroupRFF: FunctionComponent<FieldRenderProps<CheckBoxGroupValue>> = (props) => {
  const {
    input: { value, onChange, name },
    meta: { touched, error, active },
    label,
    labelSecondary,
    subLabel,
    largeLabel,
    largeSubLabel,
    labelAlignment,
    readOnly,
    options,
    buttonType = CheckboxButtonType.TEXT_OUTLINE,
    baseInputStyle,
    labelStyle,
    labelContainerStyle,
    inputContainerStyle,
    customTextStyle,
    noneOfTheAboveCheckboxId,
    selectAllCheckboxId,
    gridButtonsGap,
    limitSelected,
    hideBaseInput,
  } = props
  return (
    <CheckboxGroup
      value={value}
      onChange={onChange}
      name={name}
      label={label}
      labelSecondary={labelSecondary}
      subLabel={subLabel}
      largeLabel={largeLabel}
      largeSubLabel={largeSubLabel}
      labelAlignment={labelAlignment}
      error={touched && error}
      options={options}
      readOnly={readOnly}
      buttonType={buttonType}
      active={active}
      baseInputStyle={baseInputStyle}
      labelStyle={labelStyle}
      labelContainerStyle={labelContainerStyle}
      inputContainerStyle={inputContainerStyle}
      customTextStyle={customTextStyle}
      noneOfTheAboveCheckboxId={noneOfTheAboveCheckboxId}
      selectAllCheckboxId={selectAllCheckboxId}
      gridButtonsGap={gridButtonsGap}
      limitSelected={limitSelected}
      hideBaseInput={hideBaseInput}
    />
  )
}
export type CheckBoxGroupValue = Array<string | boolean>

export type CheckBoxGroupOptionProps = {
  id: string
  text: string | React.ReactNode
  // Union type includes { value: React.ReactNode }[] for backward compatibility with MemberPreferencesAccordion.
  // Union type includes { text: React.ReactNode; href: string } for links.
  // Value type is for legacy formats and are filtered out.
  description?:
    | string
    | { value: React.ReactNode }[]
    | {
        text: React.ReactNode
        onPress: () => void
        visible: boolean
      }
  ImageComponent?: React.ComponentType<CheckImageComponentProps>
  group?: number
  buttonType?: CheckboxButtonType
  badge?: string
  readOnly?: boolean
  SubTextField?: React.ComponentType<any>
  titleColor?: string
}

type CheckBoxGroupProps = {
  label?: string
  subLabel?: string
  largeLabel?: boolean
  largeSubLabel?: boolean
  labelSecondary?: string
  labelAlignment?: CSS.Properties['alignItems']
  value: CheckBoxGroupValue
  onChange: (arg: CheckBoxGroupValue) => void
  error?: string
  name?: string
  readOnly?: boolean
  options: Array<CheckBoxGroupOptionProps>
  buttonType?: CheckboxButtonType
  active?: boolean
  baseInputStyle?: ViewStyle
  labelStyle?: ViewStyle
  labelContainerStyle?: ViewStyle
  inputContainerStyle?: ViewStyle
  customTextStyle?: TextStyle
  noneOfTheAboveCheckboxId?: string
  selectAllCheckboxId?: string
  gridButtonsGap?: number
  parentContainerWidth?: number
  limitSelected?: number
  hideBaseInput?: boolean
  gridCheckboxSize?: GridCheckboxSize
  checkboxInputStyle?: ViewStyle
  checkboxStyle?: ViewStyle
  customTextColorForUnselectedCheckboxTextOutlineButtonType?: string
}
